'use client'

import { useRef, useState } from 'react'
import { AlertCircle, Trash2, Upload } from 'lucide-react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@rr/ui'
import { request } from '@/lib/api/client'

export type ImportColumnType = 'text' | 'number' | 'bool'

export interface ImportColumn<T = any> {
  /** Dot path into the row data. */
  path: string
  label: string
  type: ImportColumnType
  /** Visible width (Tailwind class). */
  width?: string
  /** Optional cell renderer for non-editable display. */
  readOnly?: boolean
}

type PreviewRow<T> = {
  row: number
  data: T
  status: 'new' | 'update'
  matchedId?: string
  errors: string[]
}

type PreviewResponse<T> = {
  rows: PreviewRow<T>[]
  parseErrors: string[]
}

type CommitResponse = {
  created: number
  updated: number
  errors: { row: number; error: string }[]
}

export interface ImportPreviewDialogProps<T> {
  open: boolean
  onClose: () => void
  title: string
  /** e.g. "/platform/currencies" — endpoints are `${base}/import/preview` etc. */
  endpointBase: string
  columns: ImportColumn<T>[]
  /** Called after a successful commit so the caller can invalidate its query. */
  onCommitted: (result: CommitResponse) => void
  /** Helper to label the row in error toasts. Defaults to `Row {n}`. */
  rowLabel?: (row: T, index: number) => string
}

export default function ImportPreviewDialog<T extends Record<string, any>>({
  open,
  onClose,
  title,
  endpointBase,
  columns,
  onCommitted,
  rowLabel,
}: ImportPreviewDialogProps<T>) {
  const [stage, setStage] = useState<'pick' | 'previewing' | 'preview' | 'committing'>('pick')
  const [rows, setRows] = useState<PreviewRow<T>[]>([])
  const [parseErrors, setParseErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  function reset() {
    setStage('pick')
    setRows([])
    setParseErrors([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
  function handleClose() {
    reset()
    onClose()
  }

  async function handleFile(file: File) {
    setStage('previewing')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const result = await request<PreviewResponse<T>>(`${endpointBase}/import/preview`, {
        method: 'POST',
        body: fd,
      })
      setRows(result.rows ?? [])
      setParseErrors(result.parseErrors ?? [])
      setStage('preview')
    } catch (err: any) {
      toast(err?.message ?? 'Failed to read file', 'error')
      setStage('pick')
    }
  }

  async function handleCommit() {
    const cleanRows = rows.map((r) => r.data)
    setStage('committing')
    try {
      const result = await request<CommitResponse>(`${endpointBase}/import/commit`, {
        method: 'POST',
        body: JSON.stringify({ rows: cleanRows }),
      })
      onCommitted(result)
      const parts: string[] = []
      if (result.created) parts.push(`${result.created} created`)
      if (result.updated) parts.push(`${result.updated} updated`)
      if (result.errors?.length) parts.push(`${result.errors.length} failed`)
      toast(`Import done — ${parts.join(', ') || 'no changes'}`, result.errors?.length ? 'warning' : 'success')
      handleClose()
    } catch (err: any) {
      toast(err?.message ?? 'Commit failed', 'error')
      setStage('preview')
    }
  }

  const hasErrors = rows.some((r) => r.errors.length > 0)
  const canCommit = rows.length > 0 && !hasErrors

  return (
    <Dialog open={open} onClose={handleClose} title={title} maxWidth="3xl">
      {stage === 'pick' && (
        <div className="grid place-items-center gap-3 py-12 text-center">
          <Upload className="w-10 h-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Drop an .xlsx file or pick one to preview before importing.
          </p>
          <Button onClick={() => fileInputRef.current?.click()}>Choose file…</Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleFile(file)
            }}
          />
        </div>
      )}

      {(stage === 'previewing' || stage === 'committing') && (
        <div className="flex items-center justify-center py-16">
          <Spinner />
          <span className="ml-3 text-sm text-muted-foreground">
            {stage === 'previewing' ? 'Parsing workbook…' : 'Committing…'}
          </span>
        </div>
      )}

      {stage === 'preview' && (
        <div className="grid gap-3">
          {parseErrors.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {parseErrors.join(' · ')}
            </div>
          )}
          {hasErrors && (
            <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm text-warning">
              <AlertCircle className="inline w-4 h-4 mr-1" />
              Fix the highlighted cells before committing.
            </div>
          )}

          <div className="max-h-[60vh] overflow-auto border border-border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Row</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {columns.map((c) => (
                    <TableHead key={c.path} className={c.width}>
                      {c.label}
                    </TableHead>
                  ))}
                  <TableHead className="w-12 text-right">—</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={`${r.row}-${idx}`}>
                    <TableCell className="text-xs text-muted-foreground tnum">{r.row}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
                          r.status === 'update'
                            ? 'bg-info/10 text-info'
                            : 'bg-success/10 text-success'
                        }`}
                      >
                        {r.status}
                      </span>
                      {r.errors.length > 0 && (
                        <span title={r.errors.join('; ')} className="ml-1 inline-flex">
                          <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                        </span>
                      )}
                    </TableCell>
                    {columns.map((c) => (
                      <TableCell key={c.path}>
                        <Cell
                          column={c}
                          value={getPath(r.data, c.path)}
                          onChange={(v) => {
                            const next = [...rows]
                            const data = { ...next[idx].data }
                            setPath(data, c.path, v)
                            next[idx] = { ...next[idx], data: data as T, errors: [] }
                            setRows(next)
                          }}
                          invalid={r.errors.some((e) => e.startsWith(c.path))}
                        />
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const label = rowLabel?.(r.data, idx) ?? `Row ${r.row}`
                          if (confirm(`Remove ${label} from import?`)) {
                            setRows(rows.filter((_, i) => i !== idx))
                          }
                        }}
                        aria-label="Remove row"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-muted-foreground">
            {rows.length} row{rows.length === 1 ? '' : 's'} ·{' '}
            {rows.filter((r) => r.status === 'new').length} new ·{' '}
            {rows.filter((r) => r.status === 'update').length} update
          </p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        {stage === 'preview' && (
          <Button type="button" onClick={handleCommit} disabled={!canCommit}>
            Commit import
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  )
}

function Cell({
  column,
  value,
  onChange,
  invalid,
}: {
  column: ImportColumn
  value: any
  onChange: (v: any) => void
  invalid: boolean
}) {
  const ringClass = invalid ? 'ring-1 ring-destructive' : ''
  if (column.type === 'bool') {
    return (
      <Checkbox
        checked={Boolean(value)}
        onCheckedChange={(v) => onChange(Boolean(v))}
      />
    )
  }
  if (column.type === 'number') {
    return (
      <Input
        type="number"
        className={`h-8 ${ringClass}`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    )
  }
  return (
    <Input
      className={`h-8 ${ringClass}`}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function getPath(obj: any, path: string): any {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

function setPath(obj: any, path: string, value: any): void {
  const parts = path.split('.')
  let cur = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}
    cur = cur[k]
  }
  cur[parts[parts.length - 1]] = value
}
